import { useCallback, useEffect, useMemo, useState } from 'react'

import { ThemeContext } from './ThemeContext'
import { injectCSSVariables } from '../utils/css-variables'

import type { ThemeContextValue } from './ThemeContext'
import type { BrandConfig } from './types'

export interface ThemeProviderProps {
  brand: BrandConfig
  children: React.ReactNode
  /** 默认颜色模式，'system' 表示跟随系统 */
  defaultColorMode?: 'light' | 'dark' | 'system'
}

// 获取系统颜色模式
const getSystemColorMode = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export function ThemeProvider({
  brand,
  children,
  defaultColorMode = 'system',
}: ThemeProviderProps) {
  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() => {
    if (defaultColorMode === 'system') {
      return getSystemColorMode()
    }
    return defaultColorMode
  })

  // 监听系统颜色模式变化
  useEffect(() => {
    if (defaultColorMode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setColorMode(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [defaultColorMode])

  // 注入 CSS Variables
  useEffect(() => {
    injectCSSVariables(brand.theme)

    // 设置 data 属性
    document.documentElement.setAttribute('data-brand', brand.id)
    document.documentElement.setAttribute('data-color-mode', colorMode)

    // 设置 favicon（支持按模式切换）
    const favicon = document.querySelector<HTMLLinkElement>("link[rel*='icon']")
    if (favicon) {
      const faviconUrl =
        typeof brand.favicon === 'string'
          ? brand.favicon
          : colorMode === 'dark'
            ? brand.favicon.dark
            : brand.favicon.light
      favicon.href = faviconUrl
    }

    // 设置页面标题
    document.title = `Checkout - ${brand.name}`
  }, [brand, colorMode])

  const handleSetColorMode = useCallback((mode: 'light' | 'dark') => {
    setColorMode(mode)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      brand,
      colorMode,
      setColorMode: handleSetColorMode,
    }),
    [brand, colorMode, handleSetColorMode]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
